import { useState, useEffect, useRef } from "react";
import {
  Badge,
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Image,
  HStack,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Tag,
  TagCloseButton,
  TagLabel,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Wrap,
  WrapItem,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import supabase from "../lib/supabaseClient";
import BilingualField from "./BilingualField";
import { collectionsConfig, orderAscending } from "./collectionsConfig";
import { TECH_ICONS, techIcon } from "../utils/techIcons";

function getPrimaryLabel(row, fields) {
  // For skills use "name" directly; otherwise use first bilingual field _es
  const firstBilingual = fields.find(
    (f) => f.type === "bilingual" || f.type === "bilingual-textarea"
  );
  if (firstBilingual) {
    return row[`${firstBilingual.key}_es`] || "";
  }
  return row.name || "";
}

function buildEmptyRow(fields) {
  const row = {};
  for (const field of fields) {
    if (field.type === "bilingual" || field.type === "bilingual-textarea") {
      row[`${field.key}_es`] = "";
      row[`${field.key}_en`] = "";
    } else if (
      field.type === "csv" ||
      field.type === "images" ||
      field.type === "links" ||
      field.type === "techtags" ||
      field.type === "bilinguallist"
    ) {
      row[field.key] = [];
    } else if (field.type === "checkbox") {
      // Boolean column: an empty string fails Postgres' boolean cast on insert.
      row[field.key] = false;
    } else {
      row[field.key] = "";
    }
  }
  return row;
}

export default function CollectionEditor({ table }) {
  const config = collectionsConfig[table];
  const { fields, label } = config;

  const [rows, setRows] = useState([]);
  const [editRow, setEditRow] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Dynamic select options loaded from the editable `categories` table, keyed by scope.
  const [catOptions, setCatOptions] = useState({});
  const [iconSearch, setIconSearch] = useState("");
  // Skill names available to pick from in "techtags" fields (projects tech list).
  const [skillOptions, setSkillOptions] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const fileInputRef = useRef({});

  const loadRows = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from(table)
      .select("*")
      .order("order", { ascending: orderAscending(table) });
    setLoading(false);
    if (error) {
      toast({ title: "Error cargando datos", status: "error", isClosable: true });
    } else {
      let result = data || [];
      // Group categories visually by scope (then keep their order).
      if (config.sortByScope) {
        result = [...result].sort(
          (a, b) =>
            (a.scope || "").localeCompare(b.scope || "") || (a.order ?? 0) - (b.order ?? 0)
        );
      }
      setRows(result);
    }
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);

  // Load editable category options for any select field that uses `optionsFrom`.
  useEffect(() => {
    const scopes = [...new Set(fields.filter((f) => f.optionsFrom).map((f) => f.optionsFrom))];
    if (!scopes.length) return;
    let active = true;
    (async () => {
      const result = {};
      for (const scope of scopes) {
        const { data } = await supabase
          .from("categories")
          .select("*")
          .eq("scope", scope)
          .order("order", { ascending: true });
        if (data && data.length) {
          result[scope] = data.map((c) => ({
            value: c.value,
            label: `${c.label_es || c.value}${c.label_en ? ` / ${c.label_en}` : ""}`,
          }));
        }
      }
      if (active) setCatOptions(result);
    })();
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);

  // Load skill names for any "techtags" field (e.g. the projects tech list).
  useEffect(() => {
    if (!fields.some((f) => f.type === "techtags")) return;
    let active = true;
    supabase
      .from("skills")
      .select("name")
      .order("order", { ascending: true })
      .then(({ data }) => {
        if (active) {
          // Dedupe: the skills table may contain repeated names (e.g. two
          // "Node.js" rows), which would collide as React option keys.
          setSkillOptions([...new Set((data || []).map((s) => s.name).filter(Boolean))]);
        }
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [table]);

  const openAdd = () => {
    setEditRow(buildEmptyRow(fields));
    onOpen();
  };

  const openEdit = (row) => {
    setEditRow({ ...row });
    onOpen();
  };

  const handleClose = () => {
    setEditRow(null);
    onClose();
  };

  const handleFieldChange = (key, value) => {
    setEditRow((prev) => ({ ...prev, [key]: value }));
  };

  // Uploads a file to the media bucket and returns its public URL (or null on error).
  const uploadToMedia = async (file) => {
    // Sanitized key (extension only) — original filenames with spaces/accents
    // produce an "Invalid key" 400 from Supabase Storage.
    const ext = (file.name.split(".").pop() || "png").toLowerCase().replace(/[^a-z0-9]/g, "");
    const path = `${table}/${Date.now()}-${Math.floor(performance.now())}.${ext}`;
    const { error } = await supabase.storage
      .from("media")
      .upload(path, file, { upsert: true, contentType: file.type });
    if (error) {
      toast({ title: "Error subiendo imagen", description: error.message, status: "error", isClosable: true });
      return null;
    }
    return supabase.storage.from("media").getPublicUrl(path).data.publicUrl;
  };

  const handleImageUpload = async (key, file) => {
    if (!file) return;
    const url = await uploadToMedia(file);
    if (url) handleFieldChange(key, url);
  };

  // Append one or more uploaded images to an array field (carousel).
  const handleImagesUpload = async (key, fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    const urls = [];
    for (const f of files) {
      const url = await uploadToMedia(f);
      if (url) urls.push(url);
    }
    if (urls.length) {
      handleFieldChange(key, [...(editRow[key] || []), ...urls]);
    }
  };

  const removeFromArray = (key, index) => {
    handleFieldChange(key, (editRow[key] || []).filter((_, i) => i !== index));
  };

  const hasOrderField = fields.some((f) => f.key === "order");

  // When a chosen order collides with an existing row, push that row and every
  // following one down by 1 so orders stay unique ("insert at position").
  const makeRoomForOrder = async (chosen, selfId) => {
    if (!hasOrderField) return;
    const collision = rows.some((r) => r.id !== selfId && (r.order ?? 0) === chosen);
    if (!collision) return;
    const toShift = rows
      .filter((r) => r.id !== selfId && (r.order ?? 0) >= chosen)
      .sort((a, b) => (b.order ?? 0) - (a.order ?? 0)); // highest first
    for (const r of toShift) {
      await supabase.from(table).update({ order: (r.order ?? 0) + 1 }).eq("id", r.id);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    const isNew = !editRow.id;
    const maxOrder = rows.length > 0 ? Math.max(...rows.map((r) => r.order ?? 0)) : -1;
    const validOrder =
      editRow.order !== "" && editRow.order != null && !Number.isNaN(Number(editRow.order));

    let error;
    if (isNew) {
      const { id: _id, ...rowToInsert } = editRow;
      const chosen = validOrder ? Number(editRow.order) : maxOrder + 1;
      await makeRoomForOrder(chosen, null);
      ({ error } = await supabase.from(table).insert({ ...rowToInsert, order: chosen }));
    } else {
      const payload = { ...editRow };
      if (hasOrderField && validOrder) {
        const chosen = Number(editRow.order);
        await makeRoomForOrder(chosen, editRow.id);
        payload.order = chosen;
      } else {
        // Don't overwrite the stored order with an empty value.
        delete payload.order;
      }
      ({ error } = await supabase.from(table).update(payload).eq("id", editRow.id));
    }

    if (error) {
      toast({ title: "Error al guardar", description: error.message, status: "error", isClosable: true });
    } else {
      toast({ title: "Guardado", status: "success", isClosable: true });
      handleClose();
      await loadRows();
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    const { error } = await supabase.from(table).delete().eq("id", id);
    if (error) {
      toast({ title: "Error al eliminar", status: "error", isClosable: true });
    } else {
      toast({ title: "Eliminado", status: "success", isClosable: true });
      await loadRows();
    }
  };

  // A required field counts as filled when its value(s) are non-empty.
  const isFieldFilled = (field, row) => {
    if (!row) return false;
    const { key, type } = field;
    if (type === "bilingual" || type === "bilingual-textarea") {
      return !!(row[`${key}_es`]?.trim() && row[`${key}_en`]?.trim());
    }
    if (type === "csv" || type === "images" || type === "links") {
      return Array.isArray(row[key]) && row[key].length > 0;
    }
    if (type === "checkbox") return true;
    return !!(row[key] && String(row[key]).trim());
  };

  const isValid = fields
    .filter((f) => f.required)
    .every((f) => isFieldFilled(f, editRow));

  const renderField = (field) => {
    if (!editRow) return null;
    const { key, type } = field;

    if (type === "bilingual" || type === "bilingual-textarea") {
      return (
        <BilingualField
          key={key}
          label={field.label || key}
          required={field.required}
          valueEs={editRow[`${key}_es`] || ""}
          valueEn={editRow[`${key}_en`] || ""}
          onChangeEs={(v) => handleFieldChange(`${key}_es`, v)}
          onChangeEn={(v) => handleFieldChange(`${key}_en`, v)}
          type={type === "bilingual-textarea" ? "textarea" : "input"}
        />
      );
    }

    if (type === "text") {
      return (
        <FormControl key={key} isRequired={field.required}>
          <FormLabel>{field.label || key}</FormLabel>
          <Input
            value={editRow[key] || ""}
            onChange={(e) => handleFieldChange(key, e.target.value)}
          />
        </FormControl>
      );
    }

    if (type === "csv") {
      return (
        <FormControl key={key} isRequired={field.required}>
          <FormLabel>{field.label || key}</FormLabel>
          <Input
            value={(editRow[key] || []).join(", ")}
            onChange={(e) =>
              handleFieldChange(
                key,
                e.target.value.split(",").map((s) => s.trim()).filter(Boolean)
              )
            }
            placeholder="tag1, tag2, tag3"
          />
        </FormControl>
      );
    }

    if (type === "image") {
      return (
        <FormControl key={key} isRequired={field.required}>
          <FormLabel>{field.label || key}</FormLabel>
          {editRow[key] && (
            <Image
              src={editRow[key]}
              alt="preview"
              maxH="100px"
              mb={2}
              borderRadius="md"
              objectFit="cover"
            />
          )}
          <Input
            type="file"
            accept="image/*"
            ref={(el) => (fileInputRef.current[key] = el)}
            onChange={(e) => handleImageUpload(key, e.target.files[0])}
            p={1}
          />
        </FormControl>
      );
    }

    if (type === "images") {
      const imgs = editRow[key] || [];
      return (
        <FormControl key={key} isRequired={field.required}>
          <FormLabel>{field.label || key}</FormLabel>
          {imgs.length > 0 && (
            <Flex wrap="wrap" gap={2} mb={2}>
              {imgs.map((url, i) => (
                <Box key={i} position="relative">
                  <Image src={url} alt={`img-${i}`} boxSize="64px" objectFit="cover" borderRadius="md" />
                  <Button
                    size="xs"
                    colorScheme="red"
                    position="absolute"
                    top="-2"
                    right="-2"
                    borderRadius="full"
                    onClick={() => removeFromArray(key, i)}
                  >
                    ×
                  </Button>
                </Box>
              ))}
            </Flex>
          )}
          <Input
            type="file"
            accept="image/*"
            multiple
            onChange={(e) => handleImagesUpload(key, e.target.files)}
            p={1}
          />
        </FormControl>
      );
    }

    if (type === "bilinguallist") {
      const items = editRow[key] || [];
      const updateItem = (i, lang, val) => {
        handleFieldChange(
          key,
          items.map((it, idx) => (idx === i ? { ...it, [lang]: val } : it))
        );
      };
      return (
        <FormControl key={key} isRequired={field.required}>
          <FormLabel>{field.label || key}</FormLabel>
          <Stack spacing={2}>
            {items.map((it, i) => (
              <Flex key={i} gap={2} align="center">
                <Box flex="1">
                  <Badge mb={1}>ES</Badge>
                  <Input
                    size="sm"
                    value={it.es || ""}
                    onChange={(e) => updateItem(i, "es", e.target.value)}
                    placeholder="Punto en español"
                  />
                </Box>
                <Box flex="1">
                  <Badge mb={1}>EN</Badge>
                  <Input
                    size="sm"
                    value={it.en || ""}
                    onChange={(e) => updateItem(i, "en", e.target.value)}
                    placeholder="Bullet in English"
                  />
                </Box>
                <Button
                  size="sm"
                  colorScheme="red"
                  variant="ghost"
                  alignSelf="flex-end"
                  onClick={() => removeFromArray(key, i)}
                >
                  ×
                </Button>
              </Flex>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleFieldChange(key, [...items, { es: "", en: "" }])}
            >
              + Punto
            </Button>
          </Stack>
        </FormControl>
      );
    }

    if (type === "links") {
      const links = editRow[key] || [];
      const updateLink = (i, prop, val) => {
        const next = links.map((l, idx) => (idx === i ? { ...l, [prop]: val } : l));
        handleFieldChange(key, next);
      };
      return (
        <FormControl key={key} isRequired={field.required}>
          <FormLabel>{field.label || key}</FormLabel>
          <Stack spacing={3}>
            {links.map((l, i) => (
              <Box key={i} border="1px solid" borderColor="border.card" borderRadius="md" p={2}>
                <Flex gap={2} mb={2}>
                  <Input
                    placeholder="Etiqueta ES (ej. Enlace, iOS)"
                    value={l.label_es ?? l.label ?? ""}
                    onChange={(e) => updateLink(i, "label_es", e.target.value)}
                    size="sm"
                    flex="1"
                  />
                  <Input
                    placeholder="Label EN (e.g. Link, iOS)"
                    value={l.label_en ?? l.label ?? ""}
                    onChange={(e) => updateLink(i, "label_en", e.target.value)}
                    size="sm"
                    flex="1"
                  />
                </Flex>
                <Flex gap={2} align="center">
                  <Input
                    placeholder="https://..."
                    value={l.url || ""}
                    onChange={(e) => updateLink(i, "url", e.target.value)}
                    size="sm"
                    flex="1"
                  />
                  <Button size="sm" colorScheme="red" variant="ghost" onClick={() => removeFromArray(key, i)}>
                    ×
                  </Button>
                </Flex>
              </Box>
            ))}
            <Button
              size="sm"
              variant="outline"
              onClick={() => handleFieldChange(key, [...links, { label_es: "", label_en: "", url: "" }])}
            >
              + Enlace
            </Button>
          </Stack>
        </FormControl>
      );
    }

    if (type === "number") {
      return (
        <FormControl key={key} isRequired={field.required}>
          <FormLabel>{field.label || key}</FormLabel>
          <Input
            type="number"
            value={editRow[key] ?? ""}
            onChange={(e) =>
              handleFieldChange(key, e.target.value === "" ? "" : Number(e.target.value))
            }
          />
          {field.help && (
            <Text fontSize="xs" color="text.muted" mt={1}>
              {field.help}
            </Text>
          )}
        </FormControl>
      );
    }

    if (type === "month") {
      return (
        <FormControl key={key} isRequired={field.required}>
          <FormLabel>{field.label || key}</FormLabel>
          <Input
            type="month"
            value={editRow[key] || ""}
            onChange={(e) => handleFieldChange(key, e.target.value)}
          />
        </FormControl>
      );
    }

    if (type === "checkbox") {
      return (
        <FormControl key={key} display="flex" alignItems="center" gap={2}>
          <Checkbox
            isChecked={!!editRow[key]}
            onChange={(e) => handleFieldChange(key, e.target.checked)}
          >
            {field.label || key}
          </Checkbox>
        </FormControl>
      );
    }

    if (type === "techtags") {
      const selected = editRow[key] || [];
      const available = skillOptions.filter((s) => !selected.includes(s));
      const addTag = (v) => {
        const val = (v || "").trim();
        if (val && !selected.includes(val)) handleFieldChange(key, [...selected, val]);
      };
      return (
        <FormControl key={key} isRequired={field.required}>
          <FormLabel>{field.label || key}</FormLabel>
          {selected.length > 0 && (
            <Wrap mb={2}>
              {selected.map((t) => (
                <WrapItem key={t}>
                  <Tag size="md" colorScheme="blue" borderRadius="full">
                    <TagLabel>{t}</TagLabel>
                    <TagCloseButton
                      onClick={() =>
                        handleFieldChange(key, selected.filter((x) => x !== t))
                      }
                    />
                  </Tag>
                </WrapItem>
              ))}
            </Wrap>
          )}
          <Select
            placeholder="➕ Agregar de tus habilidades"
            value=""
            onChange={(e) => {
              addTag(e.target.value);
              e.target.value = "";
            }}
          >
            {available.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </Select>
          <Input
            mt={2}
            size="sm"
            placeholder="…o escribe una y presiona Enter"
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                addTag(e.target.value);
                e.target.value = "";
              }
            }}
          />
        </FormControl>
      );
    }

    if (type === "iconpicker") {
      const Selected = techIcon(editRow[key]);
      const q = iconSearch.trim().toLowerCase();
      const list = TECH_ICONS.filter(
        (t) => !q || t.slug.includes(q) || t.label.toLowerCase().includes(q)
      );
      return (
        <FormControl key={key} isRequired={field.required}>
          <FormLabel>{field.label || key}</FormLabel>
          <HStack mb={2}>
            {Selected ? <Selected size={22} /> : null}
            <Text fontSize="sm" color="text.muted">
              {editRow[key] ? `Seleccionado: ${editRow[key]}` : "Ninguno seleccionado"}
            </Text>
          </HStack>
          <Input
            placeholder="Buscar ícono…"
            value={iconSearch}
            onChange={(e) => setIconSearch(e.target.value)}
            size="sm"
            mb={2}
          />
          <SimpleGrid
            columns={{ base: 3, sm: 4, md: 6 }}
            spacing={2}
            maxH="220px"
            overflowY="auto"
            p={2}
            border="1px solid"
            borderColor="border.card"
            borderRadius="md"
          >
            {list.map((t) => {
              const I = techIcon(t.slug);
              const selected = editRow[key] === t.slug;
              return (
                <Button
                  key={t.slug}
                  onClick={() => handleFieldChange(key, t.slug)}
                  variant={selected ? "solid" : "outline"}
                  colorScheme={selected ? "blue" : "gray"}
                  h="auto"
                  py={2}
                  display="flex"
                  flexDirection="column"
                  gap={1}
                >
                  {I ? <I size={20} /> : <Box boxSize={5} />}
                  <Text fontSize="2xs">{t.label}</Text>
                </Button>
              );
            })}
          </SimpleGrid>
        </FormControl>
      );
    }

    if (type === "select") {
      // Prefer editable categories from the DB; fall back to static options.
      const opts =
        (field.optionsFrom && catOptions[field.optionsFrom]?.length
          ? catOptions[field.optionsFrom]
          : field.options) || [];
      return (
        <FormControl key={key} isRequired={field.required}>
          <FormLabel>{field.label || key}</FormLabel>
          <Select
            placeholder="—"
            value={editRow[key] || ""}
            onChange={(e) => handleFieldChange(key, e.target.value)}
          >
            {opts.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </FormControl>
      );
    }

    return null;
  };

  return (
    <Box>
      <Flex justify="space-between" align="center" mb={4} wrap="wrap" gap={2}>
        <Heading size="md">{label}</Heading>
        <Button colorScheme="blue" onClick={openAdd}>
          Agregar
        </Button>
      </Flex>

      {loading ? (
        <Box color="text.muted">Cargando...</Box>
      ) : (
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead>
              <Tr>
                {hasOrderField && <Th w="60px">Orden</Th>}
                <Th>Nombre</Th>
                <Th>Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((row) => (
                <Tr key={row.id}>
                  {hasOrderField && (
                    <Td fontWeight="bold" color="brand.400">
                      {row.order ?? "—"}
                    </Td>
                  )}
                  <Td>{config.listLabel ? config.listLabel(row) : getPrimaryLabel(row, fields)}</Td>
                  <Td>
                    <Stack direction="row" spacing={2}>
                      <Button size="xs" onClick={() => openEdit(row)}>
                        Editar
                      </Button>
                      <Button
                        size="xs"
                        colorScheme="red"
                        variant="outline"
                        onClick={() => handleDelete(row.id)}
                      >
                        Eliminar
                      </Button>
                    </Stack>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
          {rows.length === 0 && (
            <Box color="text.muted" mt={4} textAlign="center">
              No hay registros aún.
            </Box>
          )}
        </Box>
      )}

      <Modal isOpen={isOpen} onClose={handleClose} scrollBehavior="inside" size="xl">
        <ModalOverlay />
        <ModalContent mx={{ base: 3, md: "auto" }}>
          <ModalHeader>{editRow?.id ? "Editar" : "Agregar"}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Text fontSize="sm" color="text.muted" mb={4}>
              <Box as="span" color="red.400">*</Box> Campos obligatorios
            </Text>
            <Stack spacing={4}>
              {fields.map((field) => renderField(field))}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button mr={3} onClick={handleClose} variant="ghost">
              Cancelar
            </Button>
            <Button colorScheme="blue" onClick={handleSave} isLoading={saving} isDisabled={!isValid}>
              Guardar
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}
